import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import NotificationBadge from '../../../components/ui/NotificationBadge';

const WorkshopCard = ({ workshop, onEnroll, onViewDetails }) => {
  const getDifficultyColor = (level) => {
    const colors = {
      Beginner: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      Intermediate: 'bg-amber-100 text-amber-800 border border-amber-200',
      Advanced: 'bg-rose-100 text-rose-800 border border-rose-200'
    };
    return colors?.[level] || colors?.Beginner;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-smooth">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={workshop?.image}
          alt={workshop?.imageAlt}
          className="w-full h-full object-cover"
        />
        {workshop?.isNew && (
          <div className="absolute top-3 right-3">
            <NotificationBadge count={1} variant="accent" size="sm" />
          </div>
        )}
        {workshop?.difficulty && (
          <div className="absolute bottom-3 left-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(workshop?.difficulty)}`}>
              {workshop?.difficulty}
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-foreground line-clamp-2 flex-1">
            {workshop?.title}
          </h3>
          {workshop?.verified && (
            <Icon name="BadgeCheck" size={20} color="var(--color-primary)" className="ml-2 flex-shrink-0" />
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {workshop?.description}
        </p>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
            <Image
              src={workshop?.organizerImage || workshop?.instructorImage}
              alt={workshop?.organizerImageAlt || workshop?.instructorImageAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {workshop?.instructor}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {workshop?.instructorTitle}
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Users" size={16} />
            <span>{workshop?.participants} participants</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Ticket" size={16} />
            <span>{(workshop?.spotsLeft ?? 0) === 0 ? 'No limit' : `${workshop?.spotsLeft} spots left`}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>Next: {formatDate(workshop?.nextSession)}</span>
          </div>
        </div>

        {workshop?.prerequisites && workshop?.prerequisites?.length > 0 && (
          <div className="mb-4 p-3 bg-muted/50 rounded-md">
            <p className="text-xs font-medium text-foreground mb-1">Prerequisites:</p>
            <p className="text-xs text-muted-foreground">{workshop?.prerequisites?.join(', ')}</p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Info"
            iconPosition="left"
            onClick={() => onViewDetails(workshop)}
          >
            Details
          </Button>
          <Button
            variant={workshop?.enrolled ? 'success' : 'default'}
            size="sm"
            fullWidth
            iconName={workshop?.enrolled ? 'Check' : 'Plus'}
            iconPosition="left"
            onClick={() => onEnroll(workshop)}
            disabled={workshop?.enrolled}
          >
            {workshop?.enrolled ? 'Enrolled' : 'Enroll'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkshopCard;